import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { PermissionService } from '../../core/security';
import { GlobalConfigService, LegacyAppLinkService, StorageService } from '../../core/services';
import { UserService } from '../../domains/user';
import { getSidebarItems } from './sidebar-item/sidebar-item.config';
import { SidebarItemDefinition } from './sidebar-item/sidebar-item.model';

const showSidebarPlaceholderItemsStorageKey = 'sidebar.showPlaceholderItems';

@Injectable({
    providedIn: 'root'
})
export class SidebarService {
    private configService = inject(GlobalConfigService);
    private legacyAppLinkService = inject(LegacyAppLinkService);
    private permissionService = inject(PermissionService);
    private router = inject(Router);
    private storageService = inject(StorageService);
    private userService = inject(UserService);

    private _isSidebarOpen = signal(false);
    /* One router subscription for the whole sidebar; items read this instead of subscribing each. */
    private _currentUrl = signal(this.router.url);
    private _sidebarItemsLibrary?: SidebarItemDefinition[];
    // Search filters the permission-pruned tree so hidden items never match.
    private _availableSidebarItems = signal<SidebarItemDefinition[]>([]);
    private _sidebarSearchTerm = signal('');
    private _activeSearchResultIndex = signal<number | null>(null);

    isSidebarOpen = this._isSidebarOpen.asReadonly();
    currentUrl = this._currentUrl.asReadonly();
    availableSidebarItems = this._availableSidebarItems.asReadonly();
    sidebarSearchTerm = this._sidebarSearchTerm.asReadonly();
    /** Normalized search terms — the single derivation everything search-related chains off. */
    searchTerms = computed(() => this.getSearchTerms(this._sidebarSearchTerm()));
    isSearching = computed(() => this.searchTerms().length > 0);
    sidebarItems = computed(() => {
        const sidebarItems = this._availableSidebarItems();
        const searchTerms = this.searchTerms();

        if (searchTerms.length === 0) {
            return sidebarItems;
        }

        return sidebarItems.map((item) => this.filterSidebarItemsRecursive(item, searchTerms, [])).filter((item): item is SidebarItemDefinition => !!item);
    });
    searchResultItems = computed(() => (this.isSearching() ? this.getNavigableSidebarItems(this.sidebarItems()) : []));
    activeSearchResult = computed(() => {
        const searchResults = this.searchResultItems();

        if (searchResults.length === 0) {
            return null;
        }

        return searchResults[this.getActiveSearchResultIndex(searchResults.length)];
    });

    constructor() {
        /* Update Sidebar Items anytime the UserContext changes */
        effect(() => {
            this.userService.userContext();
            this.refreshSidebarItems();
        });

        // Root service lives for the app's lifetime — no unsubscribe needed.
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
            this._currentUrl.set(event.urlAfterRedirects);
        });
    }

    toggleSidebar() {
        this._isSidebarOpen() ? this.closeSidebar() : this.openSidebar();
    }

    openSidebar() {
        this._isSidebarOpen.set(true);
    }

    closeSidebar() {
        // Search is one-off wayfinding — never reopen the drawer with a stale filtered tree.
        this.clearSidebarSearchTerm();
        this._isSidebarOpen.set(false);
    }

    refreshSidebarItems() {
        this._availableSidebarItems.set(this.getAvailableSidebarItems());
    }

    updateSidebarSearchTerm(searchTerm: string) {
        this._sidebarSearchTerm.set(searchTerm);
        this._activeSearchResultIndex.set(this.isSearching() ? 0 : null);
    }

    clearSidebarSearchTerm() {
        this._sidebarSearchTerm.set('');
        this._activeSearchResultIndex.set(null);
    }

    resolveExternalUrl(item: SidebarItemDefinition): string | undefined {
        return item.isLegacyDashboardLink ? this.legacyAppLinkService.buildDashboardUrl() : item.url;
    }

    selectNextSearchResult() {
        this.moveActiveSearchResult(1);
    }

    selectPreviousSearchResult() {
        this.moveActiveSearchResult(-1);
    }

    isActiveSearchResult(item: SidebarItemDefinition): boolean {
        // Reference equality suffices: rendered items and search results come from the same memoized sidebarItems() emission.
        const activeSearchResult = this.activeSearchResult();
        return activeSearchResult !== null && activeSearchResult === item;
    }

    /**
     * Used to unit test building the list of Available Items.
     */
    overrideSidebarItemsLibrary(sidebarItemsLibrary: SidebarItemDefinition[]) {
        this._sidebarItemsLibrary = sidebarItemsLibrary;

        this.refreshSidebarItems();
    }

    private getAvailableSidebarItems(): SidebarItemDefinition[] {
        if (!this._sidebarItemsLibrary) {
            this._sidebarItemsLibrary = getSidebarItems(this.configService);
        }

        return this._sidebarItemsLibrary
            .map((item) => this.getAvailableSidebarItemsRecursive(item)) // Returns the available sidebar items, or null if an item is not available.
            .filter((item): item is SidebarItemDefinition => !!item); // Filters out the nulls
    }

    private getAvailableSidebarItemsRecursive(item: SidebarItemDefinition): SidebarItemDefinition | null {
        // Keep placeholder routes available for developer reference, but hide them by default.
        if (item.isPlaceholder && !this.shouldShowSidebarPlaceholderItems()) {
            return null;
        }

        /* Don't return the item if it was disabled */
        if (item.isEnabled === false) {
            return null;
        }

        /* Don't return the item if it fails the permission check */
        if (item.securityRuleKey && !this.permissionService.hasPermission(item.securityRuleKey)) {
            return null;
        }

        /* Only include Parents that have available children */
        const availableChildren = item.items?.map((child) => this.getAvailableSidebarItemsRecursive(child)).filter((child): child is SidebarItemDefinition => !!child) ?? [];

        /* Don't return a parent if no children are available */
        if (item.items?.length && availableChildren.length === 0) {
            return null;
        }

        /* Return a copy of the item with its available children. */
        return { ...item, items: availableChildren };
    }

    private filterSidebarItemsRecursive(item: SidebarItemDefinition, searchTerms: string[], ancestors: string[]): SidebarItemDefinition | null {
        const currentAncestry = [...ancestors, item.label];
        // Group captions (non-navigable parents below root) provide context only — they are not match targets.
        const canSelfMatch = ancestors.length === 0 || !item.items?.length || this.isNavigableSidebarItem(item);
        const itemMatches = canSelfMatch && this.doesSidebarItemMatchSearch(item, searchTerms, ancestors);

        if (itemMatches) {
            return { ...item };
        }

        const matchingChildren = item.items?.map((child) => this.filterSidebarItemsRecursive(child, searchTerms, currentAncestry)).filter((child): child is SidebarItemDefinition => !!child) ?? [];

        // Keep ancestor branches so matching descendants stay visible.
        if (matchingChildren.length > 0) {
            return { ...item, items: matchingChildren };
        }

        return null;
    }

    private doesSidebarItemMatchSearch(item: SidebarItemDefinition, searchTerms: string[], ancestors: string[]): boolean {
        // Match labels + keywords + ancestor labels only (not routes/urls, per the redesign spec).
        const searchableText = this.normalizeSearchText([item.label, ...(item.keywords ?? []), ...ancestors].filter((value): value is string => !!value).join(' '));

        return searchTerms.every((term) => searchableText.includes(term));
    }

    /** Search always has a default keyboard target: the first result, also used when a stale index outlives a shrunken result list. */
    private getActiveSearchResultIndex(resultCount: number): number {
        const activeIndex = this._activeSearchResultIndex() ?? 0;
        return activeIndex < resultCount ? activeIndex : 0;
    }

    private moveActiveSearchResult(direction: 1 | -1) {
        const searchResults = this.searchResultItems();

        if (searchResults.length === 0) {
            return;
        }

        const activeIndex = this.getActiveSearchResultIndex(searchResults.length);
        this._activeSearchResultIndex.set((activeIndex + direction + searchResults.length) % searchResults.length);
    }

    private getNavigableSidebarItems(items: SidebarItemDefinition[]): SidebarItemDefinition[] {
        const results: SidebarItemDefinition[] = [];

        for (const item of items) {
            // Keyboard navigation only targets links, not grouping folders.
            if (this.isNavigableSidebarItem(item)) {
                results.push(item);
            }

            if (item.items?.length) {
                results.push(...this.getNavigableSidebarItems(item.items));
            }
        }

        return results;
    }

    private isNavigableSidebarItem(item: SidebarItemDefinition): boolean {
        return !!(item.routerLink || item.url);
    }

    private getSearchTerms(searchTerm: string): string[] {
        const normalizedSearchTerm = this.normalizeSearchText(searchTerm);
        return normalizedSearchTerm ? normalizedSearchTerm.split(' ') : [];
    }

    private normalizeSearchText(value: string): string {
        return value
            .trim()
            .toLocaleLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    private shouldShowSidebarPlaceholderItems(): boolean {
        return this.storageService.getItem<boolean>(showSidebarPlaceholderItemsStorageKey) === true && this.permissionService.hasPermission('sysAdmin');
    }
}
